/**
 * TaskDescriptionField Component
 *
 * Handles task description with rich text:
 * - Rich text formatting toolbar (bold, italic, lists, code)
 * - File/Image attachments display
 * - TextArea with markdown support
 * - File upload buttons
 *
 * Extracted from TaskForm.js for maintainability.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Input, Button, Tag, Tooltip } from 'antd';
import {
  PaperClipOutlined,
  PictureOutlined,
  CodeOutlined,
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import FileDropPasteArea from '../../utils/FileDropPasteArea';
import { allowedFileExtensions, allowedImageExtensions } from '../../configs/task.config';
import '../../styles/index.scss';

const TaskDescriptionField = ({
  form,
  currentTaskId,
  textareaRef,
  fileInputRef,
  imageInputRef,
  attachedFiles,
  attachedImages,
  setAttachedFiles,
  setAttachedImages,
  setNewAttachedFiles,
  setNewAttachedImages,
  handleFileDelete,
  handleInsertCode,
  handleKeyDown,
  insertText,
}) => {
  return (
    <Col className="task-description-field">
      <Card
        className="task-description-field__card"
        title={
          <Text className="task-description-field__card-title">TASK DESCRIPTION</Text>
        }
      >
        <Row>
          <Col span={24}>
            <Form.Item
              label={
                <div>
                  <div>Description</div>
                  <div className="task-description-field__label-hint">
                    Describe the task in detail. Supports markdown formatting.
                  </div>
                </div>
              }
              colon={false}
            >
              <div className="task-description-field__editor-wrapper">
                  <FileDropPasteArea
                    onFileDrop={(files) => {
                      const imageFiles = files.filter((file) =>
                        file.type.startsWith('image/')
                      );
                      const otherFiles = files.filter(
                        (file) => !file.type.startsWith('image/')
                      );
                      if (imageFiles.length > 0) {
                        setAttachedImages((prev) => [...prev, ...imageFiles]);
                        if (currentTaskId) {
                          setNewAttachedImages((prev) => [...prev, ...imageFiles]);
                        }
                      }
                      if (otherFiles.length > 0) {
                        setAttachedFiles((prev) => [...prev, ...otherFiles]);
                        if (currentTaskId) {
                          setNewAttachedFiles((prev) => [...prev, ...otherFiles]);
                        }
                      }
                    }}
                    className="task-description-field__drop-area"
                  >
                    {/* Formatting Toolbar */}
                    <div className="task-description-field__toolbar">
                      <Tooltip title="Bold" mouseEnterDelay={0.5}>
                        <Button
                          icon={<BoldOutlined />}
                          onClick={() => insertText({ before: '**', after: '**' })}
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Italic" mouseEnterDelay={0.5}>
                        <Button
                          icon={<ItalicOutlined />}
                          onClick={() => insertText({ before: '*', after: '*' })}
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Strikethrough" mouseEnterDelay={0.5}>
                        <Button
                          icon={<StrikethroughOutlined />}
                          onClick={() => insertText({ before: '~~', after: '~~' })}
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Ordered list" mouseEnterDelay={0.5}>
                        <Button
                          icon={<OrderedListOutlined />}
                          onClick={() =>
                            insertText({
                              transform: (selectedText, start) => {
                                if (selectedText === '') {
                                  return { text: '1. ', newStart: start + 3, newEnd: start + 3 };
                                }
                                const lines = selectedText
                                  .split('\n')
                                  .filter((line) => line.trim() !== '');
                                const numbered = lines
                                  .map((line, i) => `${i + 1}. ${line}`)
                                  .join('\n');
                                return { text: numbered, newStart: start, newEnd: start + numbered.length };
                              },
                            })
                          }
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Bulleted list" mouseEnterDelay={0.5}>
                        <Button
                          icon={<UnorderedListOutlined />}
                          onClick={() =>
                            insertText({
                              transform: (selectedText, start) => {
                                if (selectedText === '') {
                                  return { text: '- ', newStart: start + 2, newEnd: start + 2 };
                                }
                                const lines = selectedText
                                  .split('\n')
                                  .filter((line) => line.trim() !== '');
                                const dashed = lines.map((line) => `- ${line}`).join('\n');
                                return { text: dashed, newStart: start, newEnd: start + dashed.length };
                              },
                            })
                          }
                          type="text"
                        />
                      </Tooltip>
                    </div>

                    {/* Attachments Display */}
                    {((attachedFiles && attachedFiles.length > 0) ||
                      (attachedImages && attachedImages.length > 0)) && (
                      <div className="task-description-field__attachments">
                        {attachedFiles && attachedFiles.length > 0 && (
                          <Row className="task-description-field__attachment-row">
                            {attachedFiles.map((file, index) => (
                              <Tag
                                closable
                                key={index}
                                onClose={() => {
                                  if (currentTaskId) {
                                    handleFileDelete(file.id, 'file');
                                  } else {
                                    const updatedFiles = attachedFiles.filter((_, i) => i !== index);
                                    setAttachedFiles(updatedFiles);
                                  }
                                }}
                                icon={<PaperClipOutlined />}
                                className="task-description-field__attachment-tag"
                              >
                                {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
                              </Tag>
                            ))}
                          </Row>
                        )}
                        {attachedImages && attachedImages.length > 0 && (
                          <Row className="task-description-field__attachment-row">
                            {attachedImages.map((file, index) => (
                              <Tag
                                closable
                                key={index}
                                onClose={() => {
                                  if (currentTaskId) {
                                    handleFileDelete(file.id, 'image');
                                  } else {
                                    const updatedImages = attachedImages.filter((_, i) => i !== index);
                                    setAttachedImages(updatedImages);
                                  }
                                }}
                                icon={<PictureOutlined />}
                                className="task-description-field__attachment-tag"
                              >
                                {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
                              </Tag>
                            ))}
                          </Row>
                        )}
                      </div>
                    )}

                    {/* TextArea */}
                    <div className="task-description-field__textarea-wrapper">
                      <Form.Item name="taskDetails" noStyle>
                        <Input.TextArea
                          ref={textareaRef}
                          onChange={(e) => {
                            form.setFieldsValue({ taskDetails: e.target.value });
                          }}
                          onKeyDown={handleKeyDown}
                          maxLength={3000}
                          placeholder="Enter task details"
                          className={`task-description-field__textarea custom-textarea ${
                            attachedFiles?.length || attachedImages?.length
                              ? 'task-description-field__textarea--with-attachments'
                              : 'task-description-field__textarea--no-attachments'
                          }`}
                        />
                      </Form.Item>
                    </div>

                    {/* Hidden file inputs */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      multiple
                      accept={allowedFileExtensions.join(', ')}
                      onChange={(file) => {
                        const newFiles = Array.from(file.target.files);
                        if (newFiles.length > 0) {
                          setAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);
                          if (currentTaskId) {
                            setNewAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);
                          }
                        }
                      }}
                    />
                    <input
                      type="file"
                      ref={imageInputRef}
                      hidden
                      multiple
                      accept={allowedImageExtensions.join(', ')}
                      onChange={async (image) => {
                        const files = Array.from(image.target.files).filter((file) =>
                          file.type.startsWith('image/')
                        );
                        setAttachedImages((prevFiles) => [...prevFiles, ...files]);
                        if (currentTaskId) {
                          setNewAttachedImages((prevFiles) => [...prevFiles, ...files]);
                        }
                      }}
                    />
                  </FileDropPasteArea>

                {/* Attachment Buttons */}
                <Row className="task-description-field__action-row">
                  <Tooltip title="Attach file" mouseEnterDelay={0.5}>
                    <Button
                      onClick={() => fileInputRef.current.click()}
                      className="task-description-field__action-btn"
                    >
                      <PaperClipOutlined />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Attach image" mouseEnterDelay={0.5}>
                    <Button
                      onClick={() => imageInputRef.current.click()}
                      className="task-description-field__action-btn"
                    >
                      <PictureOutlined />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Code block" mouseEnterDelay={0.5}>
                    <Button
                      onClick={handleInsertCode}
                      className="task-description-field__action-btn"
                    >
                      <CodeOutlined />
                    </Button>
                  </Tooltip>
                </Row>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

TaskDescriptionField.propTypes = {
  form: PropTypes.object.isRequired,
  currentTaskId: PropTypes.string,
  textareaRef: PropTypes.object.isRequired,
  fileInputRef: PropTypes.object.isRequired,
  imageInputRef: PropTypes.object.isRequired,
  attachedFiles: PropTypes.array.isRequired,
  attachedImages: PropTypes.array.isRequired,
  setAttachedFiles: PropTypes.func.isRequired,
  setAttachedImages: PropTypes.func.isRequired,
  setNewAttachedFiles: PropTypes.func.isRequired,
  setNewAttachedImages: PropTypes.func.isRequired,
  handleFileDelete: PropTypes.func.isRequired,
  handleInsertCode: PropTypes.func.isRequired,
  handleKeyDown: PropTypes.func.isRequired,
  insertText: PropTypes.func.isRequired,
};

TaskDescriptionField.defaultProps = {
  currentTaskId: null,
};

export default TaskDescriptionField;
